import { Login } from './../models/login';
import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import { ServiceConstant } from '../utils/service-constant.util';
import { WebPushService } from './web-push.service';
import { ReservationService } from './reservation.service';
import { environment } from '../../environments/environment';
import { CHOOSE_POS_DISMISSED_KEY } from '../utils/pos-provider.util';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly webPush = inject(WebPushService);
  private readonly reservations = inject(ReservationService);
  private loggedIn = false;
  httpClient = inject(HttpClient);
  updatedUserProfile$ = new BehaviorSubject<any>(null);

  headers = new HttpHeaders({
    x_api_key: environment.apiKey,
    x_client_type: 'web',
  });

  isLoggedIn(): boolean {
    return !!localStorage.getItem('userToken');
  }

  login(token: string) {
    this.loggedIn = true;
    // localStorage.setItem('userToken', token);
  }

  logout() {
    this.loggedIn = false;
    // Before the bearer token goes: the DELETE is dispatched synchronously
    // inside stop(), so the interceptor can still authenticate it. A token
    // left registered would keep this browser buzzing for someone else.
    void this.webPush.stop();
    // The request badge's poll (FNF-308) would otherwise keep asking with no token.
    this.reservations.stopRequestPolling();
    localStorage.removeItem('userToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('menuView');
    try {
      sessionStorage.removeItem(CHOOSE_POS_DISMISSED_KEY);
    } catch {
      // Storage blocked — nothing was stored either.
    }
  }

  refreshToken(refreshToken: string): Observable<any> {
    return this.httpClient
      .post(ServiceConstant.apiUrl + `/auth/business/refresh`, {
        refresh_token: refreshToken,
      })
      .pipe(
        catchError((error) => {
          console.error('Error refreshing token:', error);
          return of(error);
        }),
      );
  }

  signInUser(login: Login): Observable<any> {
    return this.httpClient
      .post(ServiceConstant.apiUrl + `/auth/business/login`, login, { headers: this.headers })
      .pipe(
        catchError((error) => {
          console.error('Error fetching menus:', error);
          return of(error.error);
        }),
      );
  }

  changePasswordUser(password: any): Observable<any> {
    return this.httpClient
      .patch(ServiceConstant.apiUrl + `/business-users/update-password`, password, {
        headers: this.headers,
      })
      .pipe(
        catchError((error) => {
          console.error('Change password:', error);
          return of(error);
        }),
      );
  }

  getCurrentUser(): Observable<any> {
    return this.httpClient.get(ServiceConstant.apiUrl + `/business-users/current-user`).pipe(
      catchError((error) => {
        console.error('Change password:', error);
        return of(error);
      }),
    );
  }

  changeUserProfile(profile: any): Observable<any> {
    return this.httpClient
      .patch(ServiceConstant.apiUrl + `/business-users/update-profile`, profile, {
        headers: this.headers,
      })
      .pipe(
        catchError((error) => {
          console.error('Change profile details:', error);
          return of(error);
        }),
      );
  }

  /**
   * Exchanges a platform level token (super admin or platform support) for one
   * scoped to the selected restaurant.
   */
  signInSuperAdminUser(restaurantId: string): Observable<any> {
    return this.httpClient
      .post(
        ServiceConstant.apiUrl + `/auth/business/${restaurantId}/login`,
        {},
        { headers: this.headers },
      )
      .pipe(
        catchError((error) => {
          console.error('Error signing in super admin user:', error);
          return of(error.error);
        }),
      );
  }

  resetPasswordUser(password: any): Observable<any> {
    return this.httpClient
      .patch(ServiceConstant.apiUrl + `/business-users/set-new-password`, password)
      .pipe(
        catchError((error) => {
          console.error('Reset password:', error);
          return of(error);
        }),
      );
  }
}
